import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { History, RotateCcw, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function VersionsPanel({ proposalId, sectionKey, currentContent, onRestore }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proposals/${proposalId}/versions`, { params: { section_key: sectionKey } });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [proposalId, sectionKey]);

  const snapshot = async () => {
    if (!currentContent || !currentContent.replace(/<[^>]+>/g, "").trim()) return toast.error("Nothing to snapshot yet");
    try {
      await api.post(`/proposals/${proposalId}/versions`, { section_key: sectionKey, content: currentContent });
      toast.success("Version saved");
      load();
    } catch { toast.error("Could not save version"); }
  };

  const restore = (v) => {
    if (!window.confirm("Restore this version into the editor? Your current text will be replaced.")) return;
    onRestore(v.content);
    toast.success("Version restored");
  };

  const del = async (id) => {
    await api.delete(`/proposals/${proposalId}/versions/${id}`);
    load();
  };

  return (
    <div className="card-crisp rounded-lg p-4" data-testid="versions-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl flex items-center gap-2"><History className="w-5 h-5 text-primary"/>History</div>
        <Button size="sm" variant="ghost" onClick={snapshot} title="Snapshot" data-testid="version-snap"><Save className="w-4 h-4"/></Button>
      </div>
      <div className="text-xs text-muted-foreground mb-3">Snapshots of <b>{sectionKey.replace(/_/g," ")}</b>. Restore any time.</div>
      <div className="space-y-2 max-h-[280px] overflow-auto">
        {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-xs text-muted-foreground italic">No snapshots yet — save one after a good draft.</div>}
        {items.map((v) => (
          <div key={v.id} className="border border-border rounded-md p-2 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium">{new Date(v.created_at).toLocaleString()}</div>
              <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{(v.content || "").replace(/<[^>]+>/g, " ").slice(0, 140) || "—"}</div>
            </div>
            <button onClick={() => restore(v)} title="Restore" className="text-muted-foreground hover:text-primary" data-testid={`ver-restore-${v.id}`}><RotateCcw className="w-4 h-4"/></button>
            <button onClick={() => del(v.id)} title="Delete" className="text-muted-foreground hover:text-destructive" data-testid={`ver-del-${v.id}`}><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
